import React, { useState, useContext, useEffect, useRef } from 'react';
import type { SubMenuData } from '../../../types';
import classNames from 'classnames';
import { recordImpressionByName } from '@JohnLewisPartnership/experiments-utils';
import { MobileMenuItem } from '../../mobile-menu-item/MobileMenuItem/MobileMenuItem';
import styles from './SubMenu.scss';
import { EnvVarsFeaturesContext } from '../../../../scaffold/EnvVarsFeaturesContext';
import { UserContext } from '../../../../scaffold/UserContext';
import { formatTestId } from '../../../../utils/test-id/format-testid';
import { getExperiencesWithMatchingParamValues } from '../../../../utils/monetate/getMonetateParams';
import { filterMenuData } from '../../../../utils/menu/filterMenuData';
import { MobileMenuTabs } from '../MobileMenuTabs/MobileMenuTabs';
import { NavList } from '../NavList/NavList';
import { BackButton } from '../../mobile-menu-item/BackButton/BackButton';
import { NavigationItemType } from '../../mobile-menu-item/itemTypes';
import { Category, EXTRAS_DEPARTMENT, SHOP_DEPARTMENT } from '../../constants';
import { useTopLevelTabLoopFocus } from '../../hooks/useTopLevelTabLoopFocus';
import { sortBrandsIconCategories } from '../../../utils/utils';

type SubMenuProps = {
  setActiveLevel: (level: number) => void;
  setActiveTab: (tab: Category) => void;
  activeTab: Category;
  subMenuData: SubMenuData;
  onNavigateBack?: () => void;
  onItemSelected?: (id: string | undefined, index: number) => void;
  scrollToTop?: () => void;
  hierarchy: string[];
  level: number;
  currentActiveLevel?: number;
  children?: React.ReactNode;
};

type TabLoopCloseButton = HTMLElement & {
  __tabLoopHandler?: (event: KeyboardEvent) => void;
};

const isMenuItemPayload = (value: unknown): value is MenuItem => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<MenuItem>;
  return (
    typeof candidate.name === 'string' ||
    typeof candidate.id === 'string' ||
    Array.isArray(candidate.child)
  );
};

export const SubMenu = ({
  setActiveLevel,
  setActiveTab,
  activeTab,
  subMenuData,
  onNavigateBack = undefined,
  onItemSelected = undefined,
  scrollToTop = undefined,
  hierarchy,
  level,
  currentActiveLevel = undefined,
  children = undefined,
}: SubMenuProps) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const {
    envVars: { monetate },
    features,
  } = useContext(EnvVarsFeaturesContext) as {
    envVars: { monetate?: { channel?: string } };
    features: Features;
  };
  const {
    userState: {
      user: { monetateId },
    },
  } = useContext(UserContext) as {
    userState: {
      user: {
        monetateId?: string;
      };
    };
  };

  const subMenuDataFiltered = {
    ...subMenuData,
    child: filterMenuData(subMenuData?.child as unknown as MenuItem[], [
      'MOBILE_WEB',
    ]),
  };

  if (subMenuData?.url) {
    // append an ALL link
    const allLink: MenuItem = {
      id: 'all-link',
      name: `All ${subMenuData.name}`,
      url: subMenuData.url,
      includedInMenus: ['MOBILE_WEB'],
      longColumnOnFlyout: false,
      experimentNames: [],
      type: 'NAVIGATION_LINK',
    };

    subMenuDataFiltered.child.push(allLink);
  }

  const childItems = sortBrandsIconCategories(subMenuDataFiltered.child);

  const featuredContent = subMenuData?.featuredContent || [];

  const [activeIndex, updateActiveIndex] = useState(-1);
  const [activeMenuItem, setActiveMenuItem] = useState<MenuItem | null>(null);
  const [shouldFocusPanelOnTabChange, setShouldFocusPanelOnTabChange] =
    useState(false);
  const [shouldRestoreParentItemFocus, setShouldRestoreParentItemFocus] =
    useState(false);
  const lastActivatedItemIndexRef = useRef<number | null>(null);
  const lastActivatedItemTestIdRef = useRef<string | null>(null);

  if (level === 0 && activeTab === Category.SHOP) {
    // remove the SHOP and EXTRAS department added by code
    hierarchy.splice(
      0,
      hierarchy.length,
      ...hierarchy.filter(h => h !== SHOP_DEPARTMENT && h !== EXTRAS_DEPARTMENT)
    );
  }

  const isCurrentLevelVisible =
    currentActiveLevel === undefined ? true : level === currentActiveLevel;
  const isOwnControlsTabbable = isCurrentLevelVisible && activeIndex < 0;

  const childNavigateBack = () => {
    scrollToTop?.();
    setShouldRestoreParentItemFocus(true);
    setActiveLevel(level);
    updateActiveIndex(-1);
    setActiveMenuItem(null);
  };

  const cn = classNames(styles.subMenu, {
    [styles.categoryLevelMenu]: level > 0,
  });

  const handleMobileMenuItemClick =
    (index: number, menuItem: MenuItem) => (clickedItem?: unknown) => {
      const selectedItem = isMenuItemPayload(clickedItem)
        ? clickedItem
        : menuItem;
      const { id, name = '' } = selectedItem;
      const selectedLabel = selectedItem.name || menuItem.name;

      // Inline heading children call this handler through their parent row.
      // In that path, index points to the heading row rather than the clicked child,
      // so restore by item test id instead of row index.
      lastActivatedItemIndexRef.current =
        selectedItem === menuItem ? index : null;
      lastActivatedItemTestIdRef.current = formatTestId(selectedLabel, 'nav-');

      scrollToTop?.();
      setActiveLevel(level + 1);
      updateActiveIndex(index);
      setActiveMenuItem(selectedItem);
      if (typeof onItemSelected === 'function') {
        onItemSelected(id, index);
      }

      if (level === 1) {
        recordMatchingNavCategoryImpression(name);
      }
    };

  const recordMatchingNavCategoryImpression = (name: string) => {
    const menuItemName = formatTestId(`${subMenuDataFiltered.name} ${name}`);
    if (menuItemName) {
      // Home & Garden Bedding -> home-&-garden-bedding
      const matchingExperiences = getExperiencesWithMatchingParamValues(
        features,
        'nav-categories',
        menuItemName
      );

      if (matchingExperiences.length) {
        matchingExperiences.forEach(experience => {
          recordImpressionByName(
            monetate?.channel,
            matchingExperiences as unknown as Parameters<
              typeof recordImpressionByName
            >[1],
            experience.experienceName,
            monetateId
          );
        });
      }
    }
  };

  const handleTabChange = (tab: Category) => {
    scrollToTop?.();
    setShouldFocusPanelOnTabChange(true);
    setActiveTab(tab);
  };

  const showSelectedLines =
    childItems?.some(
      (item: MenuItem) => Boolean(item?.name?.endsWith('*')) && level === 1
    ) ||
    (Boolean(subMenuDataFiltered?.name?.endsWith('*')) && level === 2);

  const itemType = [
    Category.NEW_IN,
    Category.BE_INSPIRED,
    Category.MONEY,
  ].includes(activeTab)
    ? NavigationItemType.WALL
    : NavigationItemType.DEFAULT;

  let Top = () => <></>;

  if (level > 0) {
    Top = () => (
      <BackButton
        label={subMenuDataFiltered.name}
        onClick={onNavigateBack}
        isTabbable={isOwnControlsTabbable}
      />
    );
  } else {
    Top = () => (
      <MobileMenuTabs
        activeTab={activeTab}
        isTabbable={isOwnControlsTabbable}
        onActiveTabChange={handleTabChange}
      />
    );
  }

  const { focusActiveTab } = useTopLevelTabLoopFocus({
    containerRef: menuRef,
    isEnabled: isOwnControlsTabbable,
    activeTabSelector:
      level > 0
        ? 'button[data-testid^="nav-heading-"][tabindex="0"]'
        : 'button[data-current-tab="true"][tabindex="0"]',
  });

  const handleTabLoopSentinelFocus = () => {
    const closeButton = document.getElementById(
      'mobile-menu-toggle'
    ) as TabLoopCloseButton | null;

    if (!closeButton) {
      focusActiveTab();
      return;
    }

    closeButton.__tabLoopHandler &&
      closeButton.removeEventListener('keydown', closeButton.__tabLoopHandler);

    closeButton.__tabLoopHandler = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || event.shiftKey) {
        return;
      }

      event.preventDefault();
      closeButton.__tabLoopHandler &&
        closeButton.removeEventListener(
          'keydown',
          closeButton.__tabLoopHandler
        );
      delete closeButton.__tabLoopHandler;
      focusActiveTab();
    };

    closeButton.addEventListener('keydown', closeButton.__tabLoopHandler);
    closeButton.focus();
  };

  const getFirstPanelControl = () =>
    menuRef.current?.querySelector(
      '[data-testid="submenu-nav-list"] a[href], [data-testid="submenu-nav-list"] button:not([aria-label="back to previous menu"])'
    ) as HTMLElement | null;

  useEffect(
    function focusPanelOnTopLevelTabChange() {
      if (level === 0 && !shouldFocusPanelOnTabChange) {
        return;
      }

      const timeoutId = setTimeout(() => {
        const firstPanelControl = getFirstPanelControl();

        firstPanelControl?.focus();
        setShouldFocusPanelOnTabChange(false);
      }, 100);

      return function clearPanelFocusTimeout() {
        clearTimeout(timeoutId);
      };
    },
    [activeTab, level, shouldFocusPanelOnTabChange]
  );

  useEffect(
    function restoreFocusToActivatedParentItem() {
      if (!shouldRestoreParentItemFocus) {
        return;
      }

      const testId = lastActivatedItemTestIdRef.current;
      const lastActivatedIndex = lastActivatedItemIndexRef.current;

      const submenuList = menuRef.current?.querySelector(
        '[data-testid="submenu-nav-list"]'
      );
      const listItemControls = submenuList?.querySelectorAll('a[href], button');

      if (
        typeof lastActivatedIndex === 'number' &&
        listItemControls?.[lastActivatedIndex]
      ) {
        (listItemControls[lastActivatedIndex] as HTMLElement).focus();
        setShouldRestoreParentItemFocus(false);
        return;
      }

      if (!testId) {
        setShouldRestoreParentItemFocus(false);
        return;
      }

      const activatedItem = menuRef.current?.querySelector(
        `[data-testid="${testId}"]`
      ) as HTMLElement | null;
      activatedItem?.focus();
      setShouldRestoreParentItemFocus(false);
    },
    [shouldRestoreParentItemFocus, activeIndex]
  );

  useEffect(
    function removeHiddenLevelFromTabOrder() {
      if (isOwnControlsTabbable || !menuRef.current) {
        return;
      }

      const ownFocusableControls = Array.from(
        menuRef.current.querySelectorAll<HTMLElement>('a[href], button')
      ).filter(control => control.closest('[role="menu"]') === menuRef.current);

      ownFocusableControls.forEach(control => {
        control.setAttribute('tabindex', '-1');
      });
    },
    [isOwnControlsTabbable]
  );

  const handleSubMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab' && event.shiftKey) {
      if (
        level === 0 &&
        isOwnControlsTabbable &&
        event.target === getFirstPanelControl()
      ) {
        event.preventDefault();
        focusActiveTab();
      }
      return;
    }

    if (event.key !== 'Escape') {
      return;
    }

    if (level === 0) {
      event.preventDefault();
      const closeButton = document.getElementById('mobile-menu-toggle');
      closeButton?.click();
      return;
    }

    if (typeof onNavigateBack !== 'function') {
      return;
    }

    event.preventDefault();
    onNavigateBack();
  };

  // rearrange so the first and second link appear in the larger positions to match LG
  if (itemType === NavigationItemType.WALL && childItems.length > 5) {
    const item = childItems.splice(1, 1)[0];
    childItems.splice(5, 0, item);
  }

  return (
    <>
      <div
        ref={menuRef}
        className={cn}
        role="menu"
        onKeyDown={handleSubMenuKeyDown}
        data-testid={formatTestId(subMenuDataFiltered.name, 'submenu-')}
      >
        <Top />
        {featuredContent.length ? (
          <NavList
            itemType={NavigationItemType.FEATURED}
            name={subMenuDataFiltered.name}
          >
            {featuredContent.map((item, index) => {
              const { display, targetUrl, imageUrl } = item;

              return (
                <MobileMenuItem
                  index={index}
                  key={`${level}-${index}`}
                  label={display}
                  hierarchy={hierarchy}
                  itemType={NavigationItemType.FEATURED}
                  url={targetUrl}
                  imageUrl={imageUrl}
                />
              );
            })}
          </NavList>
        ) : (
          ''
        )}

        {childItems.length > 0 && (
          <NavList itemType={itemType} name={subMenuDataFiltered.name}>
            {childItems.map((menuItem: MenuItem, index: number) => {
              const { name, url, child, type, imageUrl } = menuItem;

              const handleMobileMenuItemClickIndex = handleMobileMenuItemClick(
                index,
                menuItem
              );
              const activeChildMenuItem =
                activeMenuItem || childItems[activeIndex] || menuItem;

              return (
                <MobileMenuItem
                  index={index}
                  key={`${level}-${index}`}
                  label={name}
                  itemType={itemType}
                  url={url}
                  imageUrl={imageUrl}
                  onClick={handleMobileMenuItemClickIndex}
                  childItems={child}
                  type={type}
                  hierarchy={hierarchy}
                  level={level}
                  activeTab={activeTab}
                  isTabbable={isOwnControlsTabbable}
                >
                  {activeIndex === index && (
                    <SubMenu
                      setActiveLevel={setActiveLevel}
                      setActiveTab={setActiveTab}
                      activeTab={activeTab}
                      level={level + 1}
                      currentActiveLevel={currentActiveLevel}
                      subMenuData={{
                        ...(activeMenuItem || childItems[activeIndex]),
                      }}
                      onItemSelected={onItemSelected}
                      scrollToTop={scrollToTop}
                      onNavigateBack={childNavigateBack}
                      hierarchy={[
                        ...hierarchy,
                        activeChildMenuItem.name || name,
                      ]}
                    />
                  )}
                </MobileMenuItem>
              );
            })}
          </NavList>
        )}
        {showSelectedLines && (
          <span
            className={styles.selectedLines}
            aria-label="Selected lines only"
          >
            {' '}
            * Selected lines
          </span>
        )}
        {level === 0 && isOwnControlsTabbable ? children : null}
        {isOwnControlsTabbable && (
          <button
            type="button"
            className={styles.tabLoopSentinel}
            data-testid="submenu-tab-loop-sentinel"
            onFocus={handleTabLoopSentinelFocus}
            aria-label={
              level > 0 ? 'Return to previous menu' : 'Return to current tab'
            }
          />
        )}
      </div>
    </>
  );
};
